import { notFound } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { z } from 'zod';

import config from '#/config';
import { type BlogPost, blog_posts } from '#/content';

import { getCompanyEmployee, listCompanyFounders } from './content-company';

const listBlogPostsSchema = z
  .object({
    fields: z
      .object({
        date: z.object({
          from: z.string(),
          to: z.string().optional(),
        }),
      })
      .optional(),
    limit: z.number().optional(),
  })
  .optional();

/**
 * List blog posts.
 *
 * @param data.fields (optional) fields to filter by.
 * @param data.limit (optional) The number of blog posts to return.
 * @returns The list of blog posts.
 */
export const listBlogPosts = createServerFn({
  method: 'GET',
})
  .validator((payload: unknown) => listBlogPostsSchema.parse(payload))
  .handler(async (ctx) => {
    const authors = await listCompanyFounders();
    return _.chain(blog_posts)
      .filter((post) => {
        if (ctx.data?.fields?.date) {
          const { from, to } = ctx.data.fields.date;

          if (from && post.date < from) {
            return false;
          }

          if (to && post.date > to) {
            return false;
          }
        }

        return true;
      })
      .map((post) => {
        const author = authors.find((author) => author.id === post.authorId);
        return author ? { ...post, author } : null;
      })
      .compact()
      .filter((post) => isBlogPostLive(post))
      .take(ctx.data?.limit ?? 16)
      .sortBy((post) => post.date)
      .map((post) => ({
        ...post,
        published: new Date(post.date).getTime() <= Date.now(),
      }))
      .reverse()
      .value();
  });

const getBlogPostSchema = z.object({
  id: z.string(),
});

/**
 * Get a blog post.
 *
 * @param data.id The ID of the blog post.
 * @returns The blog post.
 */
export const getBlogPost = createServerFn({
  method: 'GET',
})
  .validator((data: unknown) => getBlogPostSchema.parse(data))
  .handler(async (ctx) => {
    const { id } = ctx.data;
    const post = blog_posts.find((post) => post.id === id);

    if (!post || !isBlogPostLive(post)) {
      throw notFound({
        data: { id },
      });
    }

    const author = await getCompanyEmployee({ data: { id: post.authorId } });

    if (!author) {
      throw notFound({
        data: { id: post.authorId },
      });
    }

    return {
      ...post,
      author,
    };
  });

/*
|------------------
| Internals
|------------------
*/

function isBlogPostLive(post: BlogPost): boolean {
  if (['staging', 'local'].includes(config.environment)) {
    return true;
  }

  const now = DateTime.now();
  const postDatetime = DateTime.fromISO(post.date);
  return postDatetime <= now;
}
